/**
 * Fetal Growth Chart Component
 * Displays EFW on Hadlock growth curve
 */

import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
} from '@mui/material';
import { generateGrowthCurve, getExpectedEFW } from '../../utils/obCalculations';

// Simple SVG-based growth chart
const FetalGrowthChart = ({
  gaWeeks,
  efwGrams,
  percentile,
  previousMeasurements = [], // Array of { ga, efw, date }
  width = 400,
  height = 280,
}) => {
  const curve = useMemo(() => generateGrowthCurve(14, 42), []);

  // Chart dimensions
  const padding = { top: 20, right: 30, bottom: 40, left: 50 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Scales
  const minGA = 14;
  const maxGA = 42;
  const maxWeight = 4500;

  const xScale = (ga) => padding.left + ((ga - minGA) / (maxGA - minGA)) * chartWidth;
  const yScale = (weight) => padding.top + chartHeight - (weight / maxWeight) * chartHeight;

  // Generate path for percentile line
  const generatePath = (data) => {
    return data.map((val, i) => {
      const x = xScale(curve.weeks[i]);
      const y = yScale(val);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');
  };

  // Current point
  const currentPoint = gaWeeks && efwGrams ? {
    x: xScale(gaWeeks),
    y: yScale(efwGrams),
  } : null;

  // Determine percentile color
  const getPercentileColor = () => {
    if (!percentile) return '#2196f3';
    if (percentile < 3 || percentile > 97) return '#f44336';
    if (percentile < 10 || percentile > 90) return '#ff9800';
    return '#4caf50';
  };

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" color="textSecondary">
          Fetal Growth Chart
        </Typography>
        {percentile && (
          <Chip
            label={`${percentile}th percentile`}
            size="small"
            sx={{
              bgcolor: `${getPercentileColor()}20`,
              color: getPercentileColor(),
              fontWeight: 'bold',
            }}
          />
        )}
      </Box>

      <svg width={width} height={height} style={{ display: 'block', margin: '0 auto' }}>
        {/* Grid lines */}
        {[0, 1000, 2000, 3000, 4000].map((weight) => (
          <g key={weight}>
            <line
              x1={padding.left}
              y1={yScale(weight)}
              x2={width - padding.right}
              y2={yScale(weight)}
              stroke="#f0f0f0"
              strokeDasharray="2,2"
            />
            <text
              x={padding.left - 5}
              y={yScale(weight) + 4}
              textAnchor="end"
              fontSize="10"
              fill="#999"
            >
              {weight >= 1000 ? `${weight / 1000}kg` : weight}
            </text>
          </g>
        ))}

        {/* GA grid lines */}
        {[20, 24, 28, 32, 36, 40].map((ga) => (
          <g key={ga}>
            <line
              x1={xScale(ga)}
              y1={padding.top}
              x2={xScale(ga)}
              y2={height - padding.bottom}
              stroke="#f0f0f0"
              strokeDasharray="2,2"
            />
            <text
              x={xScale(ga)}
              y={height - padding.bottom + 15}
              textAnchor="middle"
              fontSize="10"
              fill="#999"
            >
              {ga}w
            </text>
          </g>
        ))}

        {/* Percentile curves */}
        {/* 3rd percentile */}
        <path
          d={generatePath(curve.p3)}
          fill="none"
          stroke="#ffcdd2"
          strokeWidth="1"
          strokeDasharray="4,2"
        />
        {/* 10th percentile */}
        <path
          d={generatePath(curve.p10)}
          fill="none"
          stroke="#ffe0b2"
          strokeWidth="1.5"
        />
        {/* 50th percentile */}
        <path
          d={generatePath(curve.p50)}
          fill="none"
          stroke="#4caf50"
          strokeWidth="2"
        />
        {/* 90th percentile */}
        <path
          d={generatePath(curve.p90)}
          fill="none"
          stroke="#ffe0b2"
          strokeWidth="1.5"
        />
        {/* 97th percentile */}
        <path
          d={generatePath(curve.p97)}
          fill="none"
          stroke="#ffcdd2"
          strokeWidth="1"
          strokeDasharray="4,2"
        />

        {/* Shaded normal zone (10-90th) */}
        <path
          d={`${generatePath(curve.p10)} ${generatePath([...curve.p90].reverse()).replace('M', 'L')} Z`}
          fill="#e8f5e9"
          fillOpacity="0.3"
        />

        {/* Previous measurements */}
        {previousMeasurements.map((m, i) => (
          <circle
            key={i}
            cx={xScale(m.ga)}
            cy={yScale(m.efw)}
            r={4}
            fill="#9e9e9e"
            stroke="#fff"
            strokeWidth="1"
          />
        ))}

        {/* Connect previous measurements */}
        {previousMeasurements.length > 1 && (
          <path
            d={previousMeasurements.map((m, i) => (
              `${i === 0 ? 'M' : 'L'} ${xScale(m.ga)} ${yScale(m.efw)}`
            )).join(' ')}
            fill="none"
            stroke="#9e9e9e"
            strokeWidth="1"
            strokeDasharray="3,3"
          />
        )}

        {/* Current measurement point */}
        {currentPoint && (
          <>
            {/* Vertical reference line */}
            <line
              x1={currentPoint.x}
              y1={padding.top}
              x2={currentPoint.x}
              y2={height - padding.bottom}
              stroke={getPercentileColor()}
              strokeWidth="1"
              strokeDasharray="4,4"
              opacity="0.5"
            />
            {/* Horizontal reference line */}
            <line
              x1={padding.left}
              y1={currentPoint.y}
              x2={width - padding.right}
              y2={currentPoint.y}
              stroke={getPercentileColor()}
              strokeWidth="1"
              strokeDasharray="4,4"
              opacity="0.5"
            />
            {/* Point */}
            <circle
              cx={currentPoint.x}
              cy={currentPoint.y}
              r={8}
              fill={getPercentileColor()}
              stroke="#fff"
              strokeWidth="2"
            />
            {/* Label */}
            <text
              x={currentPoint.x + 12}
              y={currentPoint.y - 10}
              fontSize="11"
              fontWeight="bold"
              fill={getPercentileColor()}
            >
              {efwGrams}g
            </text>
          </>
        )}

        {/* Legend */}
        <g transform={`translate(${width - padding.right - 60}, ${padding.top})`}>
          <rect x={0} y={0} width={55} height={60} fill="#fff" fillOpacity="0.9" rx="4" />
          <line x1={5} y1={12} x2={20} y2={12} stroke="#4caf50" strokeWidth="2" />
          <text x={25} y={15} fontSize="9" fill="#666">50th</text>
          <line x1={5} y1={27} x2={20} y2={27} stroke="#ffe0b2" strokeWidth="1.5" />
          <text x={25} y={30} fontSize="9" fill="#666">10/90th</text>
          <line x1={5} y1={42} x2={20} y2={42} stroke="#ffcdd2" strokeWidth="1" strokeDasharray="4,2" />
          <text x={25} y={45} fontSize="9" fill="#666">3/97th</text>
        </g>

        {/* Axis labels */}
        <text
          x={width / 2}
          y={height - 5}
          textAnchor="middle"
          fontSize="11"
          fill="#666"
        >
          Gestational Age (weeks)
        </text>
        <text
          x={15}
          y={height / 2}
          textAnchor="middle"
          fontSize="11"
          fill="#666"
          transform={`rotate(-90, 15, ${height / 2})`}
        >
          Weight (g)
        </text>
      </svg>

      {/* Expected weight display */}
      {gaWeeks && (
        <Box display="flex" justifyContent="center" gap={2} mt={1}>
          <Typography variant="caption" color="textSecondary">
            Expected at {Math.floor(gaWeeks)}w:
          </Typography>
          <Typography variant="caption">
            10th: {getExpectedEFW(gaWeeks, 10)}g
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="success.main">
            50th: {getExpectedEFW(gaWeeks, 50)}g
          </Typography>
          <Typography variant="caption">
            90th: {getExpectedEFW(gaWeeks, 90)}g
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default FetalGrowthChart;
